// Billing — 真實資料版（Route 13, PR #103）。
//
// PDF 由月結 cron 寫入 R2；前端按下下載直接 navigate 到 /v1/billing/invoices/:id/pdf
// （worker proxy R2 stream，附 audit）。

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CreditCard, Download, RefreshCw, ServerCrash, Inbox, Building2,
} from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import PageHeader from "../components/PageHeader.js";
import DataTable, { type Column } from "../components/DataTable.js";
import EmptyState from "../components/EmptyState.js";
import TenantPickerEmptyState from "../components/TenantPickerEmptyState.js";
import Skeleton from "../components/Skeleton.js";
import {
  listInvoices, listUsage, friendlyBillingError, invoicePdfUrl,
  type InvoiceRow, type UsageRow,
} from "../lib/billingApi.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";

const STATUS_BADGE: Record<string, string> = {
  draft: "badge-default", due: "badge-warning", paid: "badge-success",
  overdue: "badge-danger", void: "badge-default",
};
const STATUS_LABEL: Record<string, string> = {
  draft: "草稿", due: "待付款", paid: "已付款", overdue: "逾期", void: "作廢",
};

function fmtAmount(cents: number, currency = "TWD"): string {
  try { return new Intl.NumberFormat("zh-TW", { style: "currency", currency, maximumFractionDigits: 0 }).format(cents / 100); }
  catch { return `${(cents / 100).toFixed(0)} ${currency}`; }
}

type ListState =
  | { status: "loading" }
  | { status: "ready" }
  | { status: "error"; message: string };

export default function Billing(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const [state, setState] = useState<ListState>({ status: "loading" });
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [usage, setUsage] = useState<UsageRow[]>([]);

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const [inv, u] = await Promise.all([listInvoices(tenantParam), listUsage(tenantParam)]);
      setInvoices(inv.items);
      setUsage(u.items);
      setState({ status: "ready" });
    } catch (err) { setState({ status: "error", message: friendlyBillingError(err) }); }
  }, [tenantParam]);
  useEffect(() => { void load(); }, [load]);

  const usageChartData = useMemo(() => usage.map((u) => ({ period: u.period, locations: u.locations })), [usage]);

  const columns: Column<InvoiceRow>[] = useMemo(() => [
    {
      key: "period", header: "期間", primary: true, width: "20%",
      render: (i) => <span className="font-mono tabular-nums text-text-primary">{i.period}</span>,
    },
    { key: "locations", header: "計費地點",
      render: (i) => <span className="font-mono tabular-nums text-text-secondary">{i.locations}</span> },
    { key: "amount", header: "金額",
      render: (i) => <span className="font-mono tabular-nums text-text-primary">{fmtAmount(i.amount_cents, i.currency)}</span> },
    { key: "status", header: "狀態",
      render: (i) => <span className={STATUS_BADGE[i.status] ?? "badge-default"}>{STATUS_LABEL[i.status] ?? i.status}</span> },
    {
      key: "pdf", header: "", width: "1%",
      render: (i) => i.pdf_r2_key ? (
        <a className="btn-ghost text-xs h-7 inline-flex items-center" href={invoicePdfUrl(i.id, tenantParam)} target="_blank" rel="noreferrer">
          <Download className="h-3.5 w-3.5" /> PDF
        </a>
      ) : (
        <span className="text-2xs text-text-muted">尚未產生</span>
      ),
    },
  ], [tenantParam]);

  if (session.super_admin && !tenantParam) {
    return (<><PageHeader title="計費" description="月結帳單與地點用量" />
      <TenantPickerEmptyState /></>);
  }

  return (
    <>
      <PageHeader
        title="計費"
        description="月結地點用量、帳單與 PDF 下載。每月 1 號自動結算。"
        actions={
          <button className="btn-secondary text-sm" onClick={() => void load()} disabled={state.status === "loading"}>
            <RefreshCw className={`h-4 w-4 ${state.status === "loading" ? "animate-spin" : ""}`} /> 重新整理
          </button>
        }
      />

      {state.status === "loading" && <BillingSkeleton />}
      {state.status === "error" && <EmptyState icon={ServerCrash} title="計費資料載入失敗" description={state.message}
        action={<button className="btn-primary text-sm" onClick={() => void load()}>再試一次</button>} />}
      {state.status === "ready" && (
        <div className="space-y-6">
          <div className="card p-4 sm:p-5">
            <h2 className="text-base font-semibold text-text-primary mb-1">每月地點用量</h2>
            <p className="text-xs text-text-secondary mb-4">每月 1 號自動結算前一個月的活躍地點數量。</p>
            {usageChartData.length === 0 ? (
              <EmptyState icon={Inbox} title="目前還沒有用量資料" description="下個月 1 號完成第一次結算後，這裡會自動列出。" />
            ) : (
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={usageChartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis dataKey="period" tick={{ fill: "#6b6b73", fontSize: 11 }} axisLine={{ stroke: "rgba(255,255,255,0.08)" }} tickLine={false} />
                    <YAxis tick={{ fill: "#6b6b73", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: "rgba(0,212,255,0.08)" }} contentStyle={{ background: "#13131a", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, fontSize: 12, color: "#e8e8ec" }} />
                    <Bar dataKey="locations" fill="#00d4ff" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div>
            <h2 className="text-base font-semibold text-text-primary mb-3 flex items-center gap-2">
              <CreditCard className="h-4 w-4" /> 月結帳單
            </h2>
            {invoices.length === 0 ? (
              <EmptyState icon={Inbox} title="目前還沒有帳單" description="每月 1 號自動結算；過去的月份結算完成後會列在這裡。" />
            ) : (
              <DataTable rows={invoices} columns={columns} rowKey={(i) => i.id} searchableText={(i) => `${i.period} ${i.status}`} />
            )}
          </div>
        </div>
      )}
    </>
  );
}

function BillingSkeleton(): React.ReactElement {
  return (
    <div className="space-y-6">
      <div className="card p-5 space-y-4"><Skeleton className="h-4 w-40" /><Skeleton className="h-64 w-full" /></div>
      <div className="card overflow-hidden">
        <div className="p-4"><Skeleton className="h-9 w-72" /></div>
        <div className="divide-y divide-border">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-4 p-4">
              <Skeleton className="h-4 w-16" /><Skeleton className="flex-1 h-4" /><Skeleton className="h-5 w-20" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
